import request from '@/utils/request'

/**
 * 获取桥梁列表（带分页和筛选）
 * @param {Object} params 查询参数
 * @param {number} [params.pageNum=1] 页码
 * @param {number} [params.pageSize=10] 每页条数
 * @param {string} [params.bridgeName] 桥梁名称
 * @param {string} [params.bridgeType] 桥梁类型
 * @param {number} [params.status] 桥梁状态
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     list: Array<{
 *       id: number,
 *       bridgeCode: string,
 *       bridgeName: string,
 *       bridgeType: string,
 *       latitude: number,
 *       longitude: number,
 *       clearanceHeight: number,
 *       warningHeight: number,
 *       warningSpeed: number,
 *       geom: string,
 *       structureInfo: string,
 *       buildTime: string,
 *       status: number,
 *       createTime: string,
 *       updateTime: string
 *     }>,
 *     total: number,
 *     pageNum: number,
 *     pageSize: number
 *   }
 * }>} 分页的桥梁列表
 */
export function getBridgeList(params) {
  return request({
    url: '/bridge/list',
    method: 'get',
    params
  })
}

/**
 * 获取所有桥梁（不分页）
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: Array<{
 *     id: number,
 *     bridgeCode: string,
 *     bridgeName: string,
 *     bridgeType: string,
 *     latitude: number,
 *     longitude: number,
 *     clearanceHeight: number,
 *     warningHeight: number,
 *     warningSpeed: number,
 *     geom: string,
 *     structureInfo: string,
 *     buildTime: string,
 *     status: number,
 *     createTime: string,
 *     updateTime: string
 *   }>
 * }>} 所有桥梁列表
 */
export function getAllBridges() {
  return request({
    url: '/bridge',
    method: 'get'
  })
}

/**
 * 根据ID获取桥梁详情
 * @param {number} id 桥梁ID
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     id: number,
 *     bridgeCode: string,
 *     bridgeName: string,
 *     bridgeType: string,
 *     latitude: number,
 *     longitude: number,
 *     clearanceHeight: number,
 *     warningHeight: number,
 *     warningSpeed: number,
 *     geom: string,
 *     structureInfo: string,
 *     buildTime: string,
 *     status: number,
 *     createTime: string,
 *     updateTime: string
 *   }
 * }>} 桥梁详情
 */
export function getBridgeById(id) {
  return request({
    url: `/bridge/${id}`,
    method: 'get'
  })
}

/**
 * 根据桥梁编码获取桥梁详情
 * @param {string} code 桥梁编码
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     id: number,
 *     bridgeCode: string,
 *     bridgeName: string,
 *     bridgeType: string,
 *     latitude: number,
 *     longitude: number,
 *     clearanceHeight: number,
 *     warningHeight: number,
 *     warningSpeed: number,
 *     geom: string,
 *     structureInfo: string,
 *     buildTime: string,
 *     status: number,
 *     createTime: string,
 *     updateTime: string
 *   }
 * }>} 桥梁详情
 */
export function getBridgeByCode(code) {
  return request({
    url: `/bridge/code/${code}`,
    method: 'get'
  })
}

/**
 * 创建桥梁
 * @param {Object} data 桥梁数据
 * @param {string} data.bridgeCode 桥梁编码，只能包含字母、数字、短横线和下划线
 * @param {string} data.bridgeName 桥梁名称
 * @param {number} data.latitude 纬度
 * @param {number} data.longitude 经度
 * @param {string} [data.bridgeType] 桥梁类型
 * @param {number} [data.clearanceHeight] 净空高度(m)
 * @param {number} [data.warningHeight] 警戒高度(m)
 * @param {number} [data.warningSpeed] 警戒速度(knot)
 * @param {string} [data.geom] 几何信息(GeoJSON格式)
 * @param {string} [data.structureInfo] 结构信息
 * @param {string} [data.buildTime] 建造时间(yyyy-MM-dd)
 * @param {number} [data.status=1] 状态：1-启用，0-禁用
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     id: number,
 *     bridgeCode: string,
 *     bridgeName: string,
 *     bridgeType: string,
 *     latitude: number,
 *     longitude: number,
 *     clearanceHeight: number,
 *     warningHeight: number,
 *     warningSpeed: number,
 *     geom: string,
 *     structureInfo: string,
 *     buildTime: string,
 *     status: number,
 *     createTime: string,
 *     updateTime: string
 *   }
 * }>} 创建的桥梁信息
 */
export function createBridge(data) {
  return request({
    url: '/bridge',
    method: 'post',
    data
  })
}

/**
 * 更新桥梁
 * @param {number} id 桥梁ID
 * @param {Object} data 桥梁数据
 * @param {string} data.bridgeCode 桥梁编码，只能包含字母、数字、短横线和下划线
 * @param {string} data.bridgeName 桥梁名称
 * @param {number} data.latitude 纬度
 * @param {number} data.longitude 经度
 * @param {string} [data.bridgeType] 桥梁类型
 * @param {number} [data.clearanceHeight] 净空高度(m)
 * @param {number} [data.warningHeight] 警戒高度(m)
 * @param {number} [data.warningSpeed] 警戒速度(knot)
 * @param {string} [data.geom] 几何信息(GeoJSON格式)
 * @param {string} [data.structureInfo] 结构信息
 * @param {string} [data.buildTime] 建造时间(yyyy-MM-dd)
 * @param {number} [data.status] 状态：1-启用，0-禁用
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     id: number,
 *     bridgeCode: string,
 *     bridgeName: string,
 *     bridgeType: string,
 *     latitude: number,
 *     longitude: number,
 *     clearanceHeight: number,
 *     warningHeight: number,
 *     warningSpeed: number,
 *     geom: string,
 *     structureInfo: string,
 *     buildTime: string,
 *     status: number,
 *     createTime: string,
 *     updateTime: string
 *   }
 * }>} 更新后的桥梁信息
 */
export function updateBridge(id, data) {
  return request({
    url: `/bridge/${id}`,
    method: 'put',
    data
  })
}

/**
 * 删除桥梁
 * @param {number} id 桥梁ID
 * @returns {Promise<{
 *   code: number,
 *   message: string
 * }>} 删除结果
 */
export function deleteBridge(id) {
  return request({
    url: `/bridge/${id}`,
    method: 'delete'
  })
}

/**
 * 获取用户关联的桥梁
 * @param {number} userId 用户ID
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: Array<{
 *     id: number,
 *     userId: number,
 *     bridgeId: number,
 *     bridgeName: string,
 *     bridgeCode: string,
 *     latitude: number,
 *     longitude: number,
 *     status: number,
 *     createTime: string,
 *     updateTime: string
 *   }>
 * }>} 用户关联的桥梁列表
 */
export function getUserBridges(userId) {
  return request({
    url: `/user/${userId}/bridges`,
    method: 'get'
  })
}

/**
 * 为用户分配桥梁
 * @param {number} userId 用户ID
 * @param {Object} data 分配数据
 * @param {Array<number>} data.bridgeIds 桥梁ID列表
 * @returns {Promise<{
 *   code: number,
 *   message: string
 * }>} 分配结果
 */
export function assignUserBridges(userId, data) {
  return request({
    url: `/user/${userId}/bridges`,
    method: 'post',
    data
  })
}

// 将API函数导出为对象格式，方便组件中使用
export default {
  getBridgeList,
  getAllBridges,
  getBridgeById,
  getBridgeByCode,
  createBridge,
  updateBridge,
  deleteBridge,
  getUserBridges,
  assignUserBridges
} 